import { useState } from 'react';

export default function HeroSection() {
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  return (
    <>
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/15e2013877f1a836ef1bba71db31bde7.jpg"
            alt="Apartment building under construction"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/25 to-black/30"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center w-full">
          <div className="max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-primary-600/90 text-white px-4 py-2 rounded-full text-sm font-semibold mb-8">
              <i className="ri-price-tag-3-line"></i>
              <span>Direct Factory Pricing</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Save 20–40% On All Construction Materials By Buying Direct with&nbsp; &nbsp;Go Global Inc.
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-white/95 mb-12 leading-relaxed max-w-3xl mx-auto">
              Steel buildings, kitchens, windows, flooring, interior and exterior finishes — delivered directly to your jobsite.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <button
                onClick={() => setShowQuoteModal(true)}
                className="w-full sm:w-auto bg-primary-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-primary-700 transition-all hover:scale-105 shadow-xl whitespace-nowrap"
              >
                <i className="ri-file-list-3-line mr-2"></i>
                Get My Project Quote
              </button>
              <button
                onClick={() => setShowPlanModal(true)}
                className="w-full sm:w-auto bg-white text-gray-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-all hover:scale-105 shadow-xl whitespace-nowrap"
              >
                <i className="ri-upload-cloud-line mr-2"></i>
                Upload My Plans
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-white/90">
              <div className="flex items-center space-x-2">
                <i className="ri-shield-check-line text-2xl text-secondary-400"></i>
                <span className="text-sm font-medium">Verified Factories</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-truck-line text-2xl text-secondary-400"></i>
                <span className="text-sm font-medium">Full Logistics Support</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-money-dollar-circle-line text-2xl text-secondary-400"></i>
                <span className="text-sm font-medium">No Middleman Markup</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/70 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Get Your Project Quote</h3>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">Fill out the form below and we'll provide you with a detailed quote showing how much you can save with direct factory pricing.</p>
              <QuoteForm onSuccess={() => setShowQuoteModal(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Plan Upload Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Upload Your Plans</h3>
              <button
                onClick={() => setShowPlanModal(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">Upload your construction plans or material list and we'll provide a detailed cost comparison showing your savings.</p>
              <PlanUploadForm onSuccess={() => setShowPlanModal(false)} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function QuoteForm({ onSuccess }: { onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    materials: '',
    timeline: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6f4ee2ff40lgbk6e90g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          materials: '',
          timeline: '',
          message: ''
        });
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage('Failed to submit. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Network error. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <form id="project-quote-request" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-1">
            Project Type *
          </label>
          <select
            id="projectType"
            name="projectType"
            required
            value={formData.projectType}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">Select project type</option>
            <option value="Custom Home">Custom Home</option>
            <option value="Multi-Family Development">Multi-Family Development</option>
            <option value="Commercial Building">Commercial Building</option>
            <option value="Renovation">Renovation</option>
            <option value="Steel Building">Steel Building</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-1">
            Project Timeline *
          </label>
          <select
            id="timeline"
            name="timeline"
            required
            value={formData.timeline}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">Select timeline</option>
            <option value="Immediate (0-3 months)">Immediate (0-3 months)</option>
            <option value="Short-term (3-6 months)">Short-term (3-6 months)</option>
            <option value="Mid-term (6-12 months)">Mid-term (6-12 months)</option>
            <option value="Long-term (12+ months)">Long-term (12+ months)</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="materials" className="block text-sm font-medium text-gray-700 mb-1">
          Materials Needed *
        </label>
        <input
          type="text"
          id="materials"
          name="materials"
          required
          placeholder="e.g., Steel building, windows, flooring, kitchens"
          value={formData.materials}
          onChange={handleChange}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
          Project Details
        </label>
        <textarea
          id="message"
          name="message"
          rows={4}
          maxLength={500}
          placeholder="Tell us about your project, budget, and any specific requirements..."
          value={formData.message}
          onChange={handleChange}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
        ></textarea>
        <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
      </div>

      {status === 'success' && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
          ✓ Quote request submitted successfully! We'll contact you within 24 hours.
        </div>
      )}

      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {status === 'submitting' ? 'Submitting...' : 'Get My Quote'}
      </button>
    </form>
  );
}

function PlanUploadForm({ onSuccess }: { onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    projectSize: '',
    budget: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) formBody.append(key, value);
      });
      formBody.append('planFiles', 'Uncollectable');

      const response = await fetch('https://readdy.ai/api/form/d6f4ee2ff40lgbk6e910', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          projectSize: '',
          budget: '',
          message: ''
        });
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage('Failed to submit. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Network error. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <form id="plan-upload-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="plan-name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name *
          </label>
          <input
            type="text"
            id="plan-name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label htmlFor="plan-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            id="plan-email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="plan-phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number *
          </label>
          <input
            type="tel"
            id="plan-phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label htmlFor="plan-company" className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            id="plan-company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="plan-projectType" className="block text-sm font-medium text-gray-700 mb-1">
            Project Type *
          </label>
          <select
            id="plan-projectType"
            name="projectType"
            required
            value={formData.projectType}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">Select project type</option>
            <option value="Custom Home">Custom Home</option>
            <option value="Multi-Family Development">Multi-Family Development</option>
            <option value="Commercial Building">Commercial Building</option>
            <option value="Renovation">Renovation</option>
            <option value="Steel Building">Steel Building</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label htmlFor="plan-projectSize" className="block text-sm font-medium text-gray-700 mb-1">
            Project Size *
          </label>
          <input
            type="text"
            id="plan-projectSize"
            name="projectSize"
            required
            placeholder="e.g., 2,500 sq ft"
            value={formData.projectSize}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div>
        <label htmlFor="plan-budget" className="block text-sm font-medium text-gray-700 mb-1">
          Estimated Budget *
        </label>
        <select
          id="plan-budget"
          name="budget"
          required
          value={formData.budget}
          onChange={handleChange}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
        >
          <option value="">Select budget range</option>
          <option value="Under $50,000">Under $50,000</option>
          <option value="$50,000 - $100,000">$50,000 - $100,000</option>
          <option value="$100,000 - $250,000">$100,000 - $250,000</option>
          <option value="$250,000 - $500,000">$250,000 - $500,000</option>
          <option value="$500,000 - $1,000,000">$500,000 - $1,000,000</option>
          <option value="Over $1,000,000">Over $1,000,000</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Upload Plans or Material List
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-500 transition-colors">
          <i className="ri-upload-cloud-2-line text-4xl text-gray-400 mb-2"></i>
          <p className="text-sm text-gray-600 mb-1">File uploads are collected separately</p>
          <p className="text-xs text-gray-500">Please email your plans to the address we'll provide after submission</p>
        </div>
      </div>

      <div>
        <label htmlFor="plan-message" className="block text-sm font-medium text-gray-700 mb-1">
          Additional Information
        </label>
        <textarea
          id="plan-message"
          name="message"
          rows={4}
          maxLength={500}
          placeholder="Tell us about your project goals, timeline, and any specific requirements..."
          value={formData.message}
          onChange={handleChange}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
        ></textarea>
        <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
      </div>

      {status === 'success' && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
          ✓ Plans submitted successfully! We'll review and send you a detailed cost comparison within 48 hours.
        </div>
      )}

      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {status === 'submitting' ? 'Submitting...' : 'Submit Plans'}
      </button>
    </form>
  );
}
